import * as pdfo from "pdfo";
import * as data from "./data";

//
// LOGIC
//

// Global variable to indicate whether a selection has been made via the menu
// This is the normal case, but a URL with hash could be entered directly
let menuSet = false;

// This central function is run on page load and hashchange
// Variable menuSet is reset to false on all exit paths
function tryHash() {
  // Only bother if there's some actual hash value
  // Otherwise skip to embed placeholder PDF
  if (window.location.hash.length > 1) {
    const parentSelect = document.getElementById(
      "parent-select",
    ) as HTMLSelectElement;
    const childSelect = document.getElementById(
      "child-select",
    ) as HTMLSelectElement;

    // Early out for the normal, menu use case
    if (
      menuSet &&
      parentSelect.selectedIndex > 0 &&
      childSelect.selectedIndex > 0
    ) {
      menuSet = false;
      embedPDF(parentSelect.value, childSelect.value);
      document.title = `${
        parentSelect.options[parentSelect.selectedIndex].text
      } – ${childSelect.options[childSelect.selectedIndex].text}`;
      return;
    }

    // Moving forward...
    // This means a URL with a hash value was entered directly

    // Regex for chapter parameter
    const chapterRe = /ch=([^&]+)/;
    const hyphenRe = /-+/g;

    // Test this regex first. If it fails, reset everything and abort
    if (!chapterRe.test(window.location.hash)) {
      embedInitial();
      reset();
      return;
    }

    // Extract chapter string; replace hyphens with spaces
    const chapterMatches = window.location.hash.match(chapterRe);
    const chapterValue =
      chapterMatches !== null
        ? chapterMatches[1].replace(hyphenRe, " ")
        : "problem";

    // Select chapter option with matching value, if any
    parentSelect.value = chapterValue;

    // If it worked, run function to populate version select
    // If it failed, reset all values and abort
    if (parentSelect.value === chapterValue) {
      bigSwitch();
    } else {
      embedInitial();
      reset();
      return;
    }

    // Regex for version parameter
    const versionRe = /ver=(.+)$/;

    // Now text this regex. If it fails, reset *almost* everything and abort.
    // I want to allow for the possibility of entering a URL that indicates a
    // chapter but not a version. The chapter select will be left pre-set, and
    // the version select pre-populated.
    if (!versionRe.test(window.location.hash)) {
      embedInitial();
      menuSet = false;
      // document.activeElement.blur()
      document.title = "Kalila Reader";
      return;
    }

    // Extract version string; replace hyphens with spaces
    const versionMatches = window.location.hash.match(versionRe);
    const versionValue =
      versionMatches !== null
        ? versionMatches[1].replace(hyphenRe, " ")
        : "problem";

    // Select version option with matching value, if any
    // If this failed, we'll deal with it shortly
    childSelect.value = versionValue;

    // If the user, having previously selected a chapter and version, sets the
    // version select back to the initial prompt option, we make sure nothing
    // gets messed up. This will also handle the unlikely event of entering the
    // URL directly with the version set to "prompt"
    if (childSelect.value === "prompt") {
      if (
        !document
          .getElementById("pdf-box")
          ?.classList.contains("pdfo-container")
      ) {
        embedInitial();
      }
      menuSet = false;
      return;
    }

    // If everything worked, run PDF embed function
    // Otherwise reset almost all values. Again, it may be possible to salvage
    // chapter selection if the version parameter was bad.
    if (childSelect.value === versionValue) {
      embedPDF(chapterValue, versionValue);
      document.title = `${
        parentSelect.options[parentSelect.selectedIndex].text
      } – ${childSelect.options[childSelect.selectedIndex].text}`;
      menuSet = false;
    } else {
      embedInitial();
      menuSet = false;
      // document.activeElement.blur()
      document.title = "Kalila Reader";
    }
  } else {
    embedInitial();
    menuSet = false;
  }
}

// Global variable for embed options
const pdfOptions = {
  // Don't assume that a "modern desktop browser" supports PDF embedding
  assumeSupport: false,
  // Try to use Google Docs PDF viewer for fallback
  // fallbackPrefix:
  //   'https://docs.google.com/viewer?embedded=true&url=https://www.theobeers.com/kwd-reader/',
  // Also try to set default zoom via PDF Open Parameters
  pdfOpenParams: {
    view: "Fit",
  },
};

// Embed selected PDF
function embedPDF(chapter: string, version: string) {
  const pdfPath = `pdf/${chapter}/${version}.pdf`;

  pdfo.embed(pdfPath, "#pdf-box", pdfOptions);

  // Show disclaimer when appropriate
  // So far this is just for Cheikho's edition
  // I'm trying to confirm that 1) a problematic version has been selected,
  // and 2) a PDF has actually been embedded
  if (
    (
      document.getElementById("child-select") as HTMLSelectElement
    ).value.includes("Cheikho") &&
    document.getElementById("pdf-box")!.classList.contains("pdfo-container")
  ) {
    document.getElementById("disclaimer")!.style.display = "unset";
  } else {
    document.getElementById("disclaimer")!.style.display = "none";
  }
}

// Embed placeholder PDF
function embedInitial() {
  pdfo.embed("pdf/dummy.pdf", "#pdf-box", pdfOptions);
}

// Reset values in case of bad hash
function reset() {
  menuSet = false;
  // document.activeElement.blur()
  document.title = "Kalila Reader";
  (
    document.getElementById("parent-select") as HTMLSelectElement
  ).selectedIndex = 0;
  (document.getElementById("child-select") as HTMLSelectElement).innerHTML = "";
}

// Big switch statement to determine how to populate the version select
function bigSwitch() {
  const parentSelect = document.getElementById(
    "parent-select",
  ) as HTMLSelectElement;
  const childSelect = document.getElementById(
    "child-select",
  ) as HTMLSelectElement;

  const parentValue = parentSelect.value;

  switch (parentValue) {
    case "01 As": {
      populate(data.As);
      break;
    }
    case "01 IH pref": {
      populate(data.IHP);
      break;
    }
    case "02 IH intro": {
      populate(data.IHI);
      break;
    }
    case "01 Saghani pref": {
      populate(data.SagP);
      break;
    }
    case "01 Pan pref": {
      populate(data.PanP);
      break;
    }
    case "01 Eugenian pref": {
      populate(data.EugP);
      break;
    }
    case "01 Capua pref": {
      populate(data.CapP);
      break;
    }
    case "01 Munshi pref": {
      populate(data.MunP);
      break;
    }
    case "01 Bukhari pref": {
      populate(data.BukP);
      break;
    }
    case "01 Tusi pref": {
      populate(data.TusP);
      break;
    }
    case "01 Kashifi pref": {
      populate(data.KasP);
      break;
    }
    case "02 Kashifi intro": {
      populate(data.KasI);
      break;
    }
    case "03 Im": {
      populate(data.Im);
      break;
    }
    case "02 Lv": {
      populate(data.Lv);
      break;
    }
    case "02 Sv": {
      populate(data.Sv);
      break;
    }
    case "04 Bu": {
      populate(data.Bu);
      break;
    }
    case "05 Lo": {
      populate(data.Lo);
      break;
    }
    case "06 Di": {
      populate(data.Di);
      break;
    }
    case "07 Rd": {
      populate(data.Rd);
      break;
    }
    case "08 Oc": {
      populate(data.Oc);
      break;
    }
    case "09 At": {
      populate(data.At);
      break;
    }
    case "10 Aw": {
      populate(data.Aw);
      break;
    }
    case "16 Kd": {
      populate(data.Kd);
      break;
    }
    case "11a Km": {
      populate(data.Km);
      break;
    }
    case "11 Mc": {
      populate(data.Mc);
      break;
    }
    case "12 Kb": {
      populate(data.Kb);
      break;
    }
    case "13 Lj": {
      populate(data.Lj);
      break;
    }
    case "17 Tg": {
      populate(data.Tg);
      break;
    }
    case "18 Ks": {
      populate(data.Ks);
      break;
    }
    case "14 Lh": {
      populate(data.Lh);
      break;
    }
    case "15 Ag": {
      populate(data.Ag);
      break;
    }
    case "21 Kw": {
      populate(data.Kw);
      break;
    }
    case "20 Df": {
      populate(data.Df);
      break;
    }
    case "19 IH concl": {
      populate(data.IHC);
      break;
    }
    case "19 Munshi concl": {
      populate(data.MunC);
      break;
    }
    case "19 Tusi concl": {
      populate(data.TusC);
      break;
    }
    default: {
      childSelect.innerHTML = "";
      break;
    }
  }
}

// Function to populate the version select with the relevant options
function populate(chapterVersions: data.Chapter) {
  const childSelect = document.getElementById(
    "child-select",
  ) as HTMLSelectElement;

  childSelect.innerHTML = "";
  childSelect.insertAdjacentHTML(
    "beforeend",
    '<option value="prompt">-- Select a version --</option>',
  );

  function appendItem(currentObject: data.Version) {
    const optionHTML = `<option value="${currentObject.value}">${currentObject.display}</option>`;
    childSelect.insertAdjacentHTML("beforeend", optionHTML);
  }

  chapterVersions.forEach(appendItem);
}

// On change of the version select, set location hash
// PDF embed is then handled via tryHash function
function setHash() {
  const chapter = (
    document.getElementById("parent-select") as HTMLSelectElement
  )?.value;
  const version = (document.getElementById("child-select") as HTMLSelectElement)
    ?.value;

  menuSet = true;

  window.location.hash = `ch=${chapter.replace(
    /\s+/g,
    "-",
  )}&ver=${version.replace(/\s+/g, "-")}`;
}

//
// EVENT HANDLING
//

// On page load, determine initial embed
window.onload = tryHash;

// Event listeners for chapter and version selection
document.getElementById("parent-select")?.addEventListener("change", bigSwitch);
document.getElementById("child-select")?.addEventListener("change", setHash);

// Event listener for hashchange
window.addEventListener("hashchange", tryHash);
